/**
 * RevenueCat integration — handles native IAP for iOS and Android.
 *
 * Architecture:
 * - iOS/Android: RevenueCat (required by App Store 3.1.1)
 * - Web: Stripe Checkout (existing flow, unchanged)
 *
 * RevenueCat product IDs map to DUEL plan IDs:
 *   pro_monthly / pro_yearly
 *   coach_monthly / coach_yearly
 *   club_starter_monthly / club_starter_yearly
 *   club_growth_monthly / club_growth_yearly
 *   club_elite_monthly / club_elite_yearly
 */

import { Platform } from 'react-native';
import { createLogger } from './logger';

const log = createLogger('REVENUECAT');

const APPLE_API_KEY = process.env.EXPO_PUBLIC_REVENUECAT_APPLE_API_KEY ?? '';
const GOOGLE_API_KEY = process.env.EXPO_PUBLIC_REVENUECAT_GOOGLE_API_KEY ?? '';

let Purchases: any = null;

function getPurchases() {
  if (Purchases) return Purchases;
  try {
    Purchases = require('react-native-purchases').default;
  } catch {
    log.warn('react-native-purchases not available (web)');
  }
  return Purchases;
}

/**
 * Initialize RevenueCat SDK. Call once at app startup on native.
 */
export async function initRevenueCat(userId?: string): Promise<void> {
  if (Platform.OS === 'web') return;

  const RC = getPurchases();
  if (!RC) return;

  const apiKey = Platform.OS === 'ios' ? APPLE_API_KEY : GOOGLE_API_KEY;
  if (!apiKey) {
    log.warn('RevenueCat API key not configured');
    return;
  }

  try {
    await RC.configure({ apiKey });
    if (userId) {
      await RC.logIn(userId);
    }
    log.info('RevenueCat initialized', { platform: Platform.OS });
  } catch (err) {
    log.error('RevenueCat init failed:', err);
  }
}

/**
 * Log in the user to RevenueCat (call after auth).
 */
export async function revenueCatLogIn(userId: string): Promise<void> {
  if (Platform.OS === 'web') return;
  const RC = getPurchases();
  if (!RC) return;
  try {
    await RC.logIn(userId);
    log.info('RevenueCat logged in', { userId: userId.slice(0, 8) });
  } catch (err) {
    log.error('RevenueCat logIn failed:', err);
  }
}

/**
 * Log out (call on sign-out).
 */
export async function revenueCatLogOut(): Promise<void> {
  if (Platform.OS === 'web') return;
  const RC = getPurchases();
  if (!RC) return;
  try {
    await RC.logOut();
  } catch (err) {
    log.error('RevenueCat logOut failed:', err);
  }
}

/**
 * Purchase a product by RevenueCat product ID.
 * Returns true on success, false on cancellation/error.
 * On success, syncs the resolved tier back to the Supabase profile so
 * useSubscription picks it up immediately without requiring a re-login.
 */
export async function purchaseProduct(productId: string): Promise<boolean> {
  if (Platform.OS === 'web') return false;
  const RC = getPurchases();
  if (!RC) return false;

  try {
    const offerings = await RC.getOfferings();
    const allPackages = offerings?.current?.availablePackages ?? [];

    // Find package matching productId
    const pkg = allPackages.find(
      (p: any) => p.product?.identifier === productId
    );

    if (!pkg) {
      log.warn('Package not found for productId:', productId);
      return false;
    }

    let { customerInfo } = await RC.purchasePackage(pkg);
    let active = customerInfo?.entitlements?.active ?? {};
    let tier = resolveEntitlementTier(active);

    // Sandbox timing guard: RC sometimes returns empty entitlements immediately after
    // purchase. Retry getCustomerInfo once after a short delay before syncing to DB.
    if (tier === 'free') {
      log.warn('Tier free immediately after purchase, retrying getCustomerInfo in 2s…');
      await new Promise<void>((resolve) => setTimeout(resolve, 2000));
      try {
        const retried = await RC.getCustomerInfo();
        active = retried?.entitlements?.active ?? {};
        tier = resolveEntitlementTier(active);
        customerInfo = retried; // use fresh customerInfo for DB sync
      } catch (retryErr) {
        log.warn('Retry getCustomerInfo failed (non-blocking):', retryErr);
      }
    }

    log.info('Purchase complete', { productId, tier, entitlements: Object.keys(active) });

    // Sync tier back to Supabase profile immediately
    await syncRevenueCatTierToProfile(customerInfo);

    // Client-side org provisioning fallback (belt-and-suspenders).
    // The RevenueCat webhook (revenuecat-webhook edge function) is the primary
    // provisioning path. This runs immediately after purchase in case the
    // webhook has not yet fired or is delayed.
    if (tier === 'org_pro') {
      try {
        const { supabase: sb } = await import('@/lib/supabase');
        const { data: { user } } = await sb.auth.getUser();
        if (user?.id) {
          const { reconcileClubOrg } = await import('@/lib/orgProvisioning');
          await reconcileClubOrg(user.id, tier);
        }
      } catch (orgErr: any) {
        log.warn('Post-purchase org reconciliation failed (non-blocking):', orgErr?.message ?? orgErr);
      }
    }

    return true;
  } catch (err: any) {
    if (err?.userCancelled) {
      log.info('Purchase cancelled by user');
      return false;
    }
    log.error('Purchase failed:', err);
    return false;
  }
}

/**
 * Sync the tier resolved from a RevenueCat customerInfo object
 * back to the Supabase profile row so the app sees it immediately.
 * Safe to call multiple times (upserts subscription_tier + subscription_expires_at).
 *
 * GUARD: if tier resolves to 'free' (e.g. entitlements temporarily empty after
 * purchase due to sandbox timing or entitlement key mismatch), we do NOT write
 * 'free' to the DB — that would overwrite a real active subscription.
 */
export async function syncRevenueCatTierToProfile(customerInfo: any): Promise<void> {
  try {
    const { supabase } = await import('@/lib/supabase');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const active = customerInfo?.entitlements?.active ?? {};
    const tier = resolveEntitlementTier(active);

    // Never write 'free' from this sync path — prevents a timing race from
    // locking users out immediately after a successful purchase.
    if (tier === 'free') {
      log.warn('syncRevenueCatTierToProfile: tier free, skipping DB write to protect existing subscription');
      return;
    }

    // Derive expiry from the most relevant active entitlement
    const entitlementKey = active['club'] ? 'club' : active['coach'] ? 'coach' : active['pro'] ? 'pro' : null;
    const expiresAt: string | null = entitlementKey
      ? (active[entitlementKey]?.expirationDate ?? null)
      : null;

    log.info('Syncing RC tier to profile', { tier, expiresAt });

    const { error } = await supabase
      .from('profiles')
      .update({
        subscription_tier: tier,
        ...(expiresAt ? { subscription_expires_at: expiresAt } : {}),
      })
      .eq('id', user.id);

    if (error) {
      log.error('Failed to sync RC tier to profile:', error);
    } else {
      log.info('RC tier synced to profile', { tier });
      // Non-blocking: send welcome notification on upgrade to paid tier
      if (tier !== 'free') {
        try {
          const { notifyWelcome } = await import('@/lib/notificationTriggers');
          await notifyWelcome(user.id);
        } catch (notifErr) {
          log.warn('notifyWelcome failed (non-blocking):', notifErr);
        }
      }
    }
  } catch (err) {
    log.error('syncRevenueCatTierToProfile error:', err);
  }
}

/**
 * Restore purchases from the store.
 */
export async function restoreRevenueCatPurchases(): Promise<{ restored: boolean; tier: string }> {
  if (Platform.OS === 'web') return { restored: false, tier: 'free' };
  const RC = getPurchases();
  if (!RC) return { restored: false, tier: 'free' };

  try {
    const customerInfo = await RC.restorePurchases();
    const active = customerInfo?.entitlements?.active ?? {};
    const tier = resolveEntitlementTier(active);
    log.info('Purchases restored', { tier, activeEntitlements: Object.keys(active) });

    // Sync restored tier back to profile
    if (tier !== 'free') {
      await syncRevenueCatTierToProfile(customerInfo);
    }

    return { restored: tier !== 'free', tier };
  } catch (err) {
    log.error('Restore failed:', err);
    return { restored: false, tier: 'free' };
  }
}

/**
 * Get current customer info and resolve tier from RevenueCat.
 * Pure read — does NOT write to the database.
 * All DB sync decisions are owned by useSubscription (with retry/downgrade guards).
 */
export async function getRevenueCatTier(): Promise<string> {
  if (Platform.OS === 'web') return 'free';
  const RC = getPurchases();
  if (!RC) return 'free';

  try {
    const customerInfo = await RC.getCustomerInfo();
    const active = customerInfo?.entitlements?.active ?? {};
    return resolveEntitlementTier(active);
  } catch {
    return 'free';
  }
}

/**
 * Map RevenueCat entitlements to DUEL subscription tiers.
 * Set up these entitlement identifiers in the RevenueCat dashboard:
 *   "pro", "coach", "club"
 */
function resolveEntitlementTier(active: Record<string, any>): string {
  if (active['club']) return 'org_pro';
  if (active['coach']) return 'coach_pro';
  if (active['pro']) return 'pro';
  return 'free';
}

/**
 * Map DUEL plan ID to RevenueCat product identifier.
 */
export function planIdToRevenueCatProductId(planId: string, interval: 'monthly' | 'yearly'): string {
  const suffix = interval === 'yearly' ? '_yearly' : '_monthly';
  const map: Record<string, string> = {
    pro: `pro${suffix}`,
    coach: `coach${suffix}`,
    club_starter: `club_starter${suffix}`,
    club_growth: `club_growth${suffix}`,
    club_performance: `club_performance${suffix}`,
    club_elite: `club_elite${suffix}`,
  };
  return map[planId] ?? `${planId}${suffix}`;
}
